"use client";

import { useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { trackContactFormSubmit, trackQuoteRequest, trackFormSubmit } from "@/lib/analytics";
import { smsAdminNewLead, getAdminSMSPrefs } from "@/lib/sms";
import { scoreLeadWithClaude, saveLeadScore } from "@/lib/leadScoring";

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  bedrooms: string;
  serviceType: string;
  message: string;
}

const initialForm: FormData = {
  name: "",
  email: "",
  phone: "",
  address: "",
  bedrooms: "",
  serviceType: "",
  message: "",
};

export default function ContactForm() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitError, setSubmitError] = useState("");

  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "Enter a valid email";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    if (!form.serviceType) newErrors.serviceType = "Please select a service";
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError("");

    try {
      const supabase = createClient();
      const leadId = crypto.randomUUID();

      // Save lead to Supabase
      const { error: dbError } = await supabase.from("leads").insert({
        id: leadId,
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        bedrooms: form.bedrooms,
        service_type: form.serviceType,
        message: form.message,
        lead_status: "new",
      });

      if (dbError) {
        console.error("DB error:", dbError.message);
        setSubmitError("Failed to submit. Please try again.");
        setSubmitting(false);
        trackFormSubmit('contact_form', 'error', form.serviceType, form.bedrooms);
        return;
      }

      // Fire-and-forget: Zapier automations (non-blocking)
      const zapierPayload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        bedrooms: form.bedrooms,
        service_type: form.serviceType,
        message: form.message,
        submitted_at: new Date().toISOString(),
      };
      const zapierHooks = [
        "https://hooks.zapier.com/hooks/catch/27191169/4y6pzjs/",
        "https://hooks.zapier.com/hooks/catch/27191169/4y6vws0/",
        "https://hooks.zapier.com/hooks/catch/27191169/4ytygyv/",
      ];
      zapierHooks.forEach((url) => {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(zapierPayload),
        }).catch((err) => console.warn("[Zapier] Webhook failed:", url, err));
      });

      // Fire-and-forget: Score lead with Claude (non-blocking)
      if (leadId) {
        const leadData = {
          name: form.name,
          phone: form.phone,
          serviceType: form.serviceType,
          bedrooms: form.bedrooms,
          message: form.message,
        };
        // Fetch admin prefs once, then fire SMS after scoring (with score) or immediately on failure (without score)
        getAdminSMSPrefs().then((adminPrefs) => {
          scoreLeadWithClaude(leadData).then((score) => {
            if (score) saveLeadScore(leadId, score);
            if (adminPrefs) {
              smsAdminNewLead({
                name: form.name,
                phone: form.phone,
                serviceType: form.serviceType,
                bedrooms: form.bedrooms,
                urgencyScore: score?.urgency_score,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          }).catch(() => {
            // Scoring failed — still send SMS without score
            if (adminPrefs) {
              smsAdminNewLead({
                name: form.name,
                phone: form.phone,
                serviceType: form.serviceType,
                bedrooms: form.bedrooms,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          });
        }).catch((smsErr) => console.warn("[SMS] Admin new lead SMS failed:", smsErr));
      }

      // Send emails via server-side API route (avoids CORS preflight issues)
      try {
        await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "contact_lead",
            data: {
              name: form.name,
              email: form.email,
              phone: form.phone,
              address: form.address,
              bedrooms: form.bedrooms,
              serviceType: form.serviceType,
              message: form.message,
            },
          }),
        });
      } catch (emailErr) {
        // Email failure is non-blocking — lead is already saved
        console.warn("Email send failed:", emailErr);
      }

      setSubmitting(false);
      setSubmitted(true);
      setForm(initialForm);
      // Track GA conversion events
      trackContactFormSubmit(form.serviceType);
      trackQuoteRequest(form.serviceType, 'contact_form');
      trackFormSubmit('contact_form', 'success', form.serviceType, form.bedrooms);
    } catch (err) {
      console.error("Submit error:", err);
      setSubmitError("Something went wrong. Please try again.");
      setSubmitting(false);
      trackFormSubmit('contact_form', 'error', form.serviceType, form.bedrooms);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-sand-medium rounded-3xl p-10 text-center shadow-luxury">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="font-heading text-2xl text-navy mb-3">Quote Request Received!</h3>
        <p className="text-navy/60 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
          Thank you! Our team will review your property details and reach out within 2 hours. Check your email for a confirmation.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="btn-navy px-8 py-3 rounded-full text-sm font-semibold"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="bg-white border border-sand-medium rounded-3xl p-8 sm:p-10 shadow-luxury">
      <h3 className="font-heading text-2xl text-navy mb-2">Request a Free Quote</h3>
      <p className="text-navy/50 text-sm mb-8">We&apos;ll respond within 2 hours during business hours.</p>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-600 text-sm">{submitError}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
        {/* Name */}
        <div>
          <label className="block text-navy text-sm font-semibold mb-2">
            Full Name <span className="text-gold">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Jennifer Martinez"
            className={`input-luxury ${errors.name ? "border-red-400 focus:border-red-400" : ""}`}
            aria-describedby={errors.name ? "name-error" : undefined}
          />
          {errors.name && <p id="name-error" className="text-red-500 text-xs mt-1.5">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-navy text-sm font-semibold mb-2">
            Email Address <span className="text-gold">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="jennifer@example.com"
            className={`input-luxury ${errors.email ? "border-red-400 focus:border-red-400" : ""}`}
            aria-describedby={errors.email ? "email-error" : undefined}
          />
          {errors.email && <p id="email-error" className="text-red-500 text-xs mt-1.5">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-navy text-sm font-semibold mb-2">
            Phone Number <span className="text-gold">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="(850) 555-0100"
            className={`input-luxury ${errors.phone ? "border-red-400 focus:border-red-400" : ""}`}
            aria-describedby={errors.phone ? "phone-error" : undefined}
          />
          {errors.phone && <p id="phone-error" className="text-red-500 text-xs mt-1.5">{errors.phone}</p>}
        </div>

        {/* Bedrooms */}
        <div>
          <label className="block text-navy text-sm font-semibold mb-2">
            Number of Bedrooms
          </label>
          <select
            name="bedrooms"
            value={form.bedrooms}
            onChange={handleChange}
            className="input-luxury"
          >
            <option value="">Select bedrooms</option>
            <option value="studio">Studio</option>
            <option value="1">1 Bedroom</option>
            <option value="2">2 Bedrooms</option>
            <option value="3">3 Bedrooms</option>
            <option value="4">4 Bedrooms</option>
            <option value="5+">5+ Bedrooms</option>
          </select>
        </div>
      </div>

      {/* Property Address */}
      <div className="mb-5">
        <label className="block text-navy text-sm font-semibold mb-2">
          Property Address
        </label>
        <input
          type="text"
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="123 Front Beach Rd, Panama City Beach, FL 32413"
          className="input-luxury"
        />
      </div>

      {/* Service Type */}
      <div className="mb-5">
        <label className="block text-navy text-sm font-semibold mb-2">
          Service Needed <span className="text-gold">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: "cleaning", label: "Cleaning Only", icon: "✨" },
            { value: "management", label: "Full Management", icon: "📊" },
            { value: "both", label: "Both Services", icon: "🏆" },
          ].map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                form.serviceType === option.value
                  ? "border-navy bg-navy/5" : "border-sand-medium bg-white hover:border-navy/30"
              }`}
            >
              <input
                type="radio"
                name="serviceType"
                value={option.value}
                checked={form.serviceType === option.value}
                onChange={handleChange}
                className="sr-only"
              />
              <span className="text-xl">{option.icon}</span>
              <span className={`text-sm font-medium ${form.serviceType === option.value ? "text-navy" : "text-navy/70"}`}>
                {option.label}
              </span>
              {form.serviceType === option.value && (
                <svg className="w-4 h-4 text-gold ml-auto shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </label>
          ))}
        </div>
        {errors.serviceType && <p className="text-red-500 text-xs mt-1.5">{errors.serviceType}</p>}
      </div>

      {/* Message */}
      <div className="mb-8">
        <label className="block text-navy text-sm font-semibold mb-2">
          Additional Details
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          rows={4}
          placeholder="Tell us about your property, current situation, or any questions you have..."
          className="input-luxury resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className={`btn-gold w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-3 ${
          submitting ? "opacity-75 cursor-not-allowed" : ""
        }`}
      >
        {submitting ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Sending Request...
          </>
        ) : (
          "Request Free Quote →"
        )}
      </button>
    </form>
  );
}